import React, { useContext, useState, useEffect } from 'react';
import { Download, Filter, X, Search, Eye, Edit, Trash2, FileText, Calendar, User, ChevronDown, History, FileSpreadsheet } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../../../context/AuthContext';
import processService from '../../../../services/processService';
import userService from '../../../../services/userService';
import exportService from '../../../../services/exportService';
import { useToast } from '../../../../context/ToastContext';
import ProcessListItems from './ProcessListItems';
import Pagination from '../../../../components/Pagination';



export default function ViewCartographs() {
  const navigate = useNavigate();
  const {logoutUser,hasRole, user, authTokens } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();
  
  const [showFiltersSection, setShowFiltersSection] = useState(false);
  const [selectedProcess, setSelectedProcess] = useState(null);

  const [filters, setFilters] = useState({
    process_name: '',
    assigned_user: '',
    probability: '',
    status: '',
    ordering: 'newest'
  });


    const [pagination, setPagination] = useState({
        count: 0,
        page: 1,
        page_size: 10,
        total_pages: 0
    });

  const [processes, setProcesses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [userSearchLoading, setUserSearchLoading] = useState(false);

  // Export modal state
  const [exportModal, setExportModal] = useState({
    isOpen: false,
    type: '', // 'pdf' or 'excel'
    loading: false,
    filters: {
      process_name: '',
      status: '',
      assigned_user: '',
      probability: '',
      created_date_from: '',
      created_date_to: '',
      process_date_from: '',
      process_date_to: '',
      control_date_from: '',
      control_date_to: '',
      ordering: 'newest'
    }
  });


  // const canViewAllProcesses = () => {
  //   return hasRole("Admin") || hasRole("RiskManager");
  // };

  const canEditProcess = () => {
    return hasRole("RiskManager");
  };

  const canDeleteProcess = () => {
    return hasRole("Admin") || hasRole("RiskManager");
  };

  // Load published processes on component mount and when filters change
  useEffect(() => {
    // if (canViewPublished()) {
      loadPublishedProcesses();
    // }
  }, [pagination.page, pagination.page_size]);

  const loadPublishedProcesses = async () => {
    setIsLoading(true);
    setError(null);
    
    try {

      const pagination_data = {
        page: pagination.page,
        page_size: pagination.page_size,
      };
      const result = await processService.getPublishedProcesses(authTokens.access, filters, pagination_data);
      
      if (result.success) {
        setProcesses(result.data.results);

        setPagination(prev => ({
          ...prev,
          count: result.data.count,
          total_pages: result.data.total_pages
        }));
      } else {
        setError(result.error);
        console.error('Failed to load published processes:', result.error);
      }
    } catch (error) {
      setError('Une erreur est survenue lors du chargement des processus');
      console.error('Error loading published processes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const statusOptions = ['draft', 'published', 'archived'];
  const probabilityOptions = ['low', 'medium', 'high', 'critical'];


    const handlePageChange = (newPage) => {
      setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handlePageSizeChange = (newPageSize) => {
      setPagination(prev => ({ 
        ...prev, 
        page_size: newPageSize, 
        page: 1 // Reset to first page
      }));
    };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      process_name: '',
      assigned_user: '',
      probability: '',
      status: '',
      ordering: 'newest'
    });

      setPagination(prev => ({ ...prev, page: 1 }));
      // Fetch will be triggered by useEffect when filters change
      setTimeout(() => loadPublishedProcesses(), 0);

  };

  const applyFilters = () => {
    console.log('Applying filters:', filters);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
    loadPublishedProcesses();
  };

  const openExportModal = (type) => {
    setExportModal({
      ...exportModal,
      isOpen: true,
      type: type,
      loading: false
    });
  };

  const closeExportModal = () => {
    setExportModal({
      ...exportModal,
      isOpen: false,
      type: '',
      loading: false
    });
  };

  const handleExportFilterChange = (field, value) => {
    setExportModal({
      ...exportModal,
      filters: {
        ...exportModal.filters,
        [field]: value
      }
    });
  };

  const handleExport = async () => {
    setExportModal({ ...exportModal, loading: true });
    
    try {
      let result;
      if (exportModal.type === 'pdf') {
        result = await exportService.exportProcessesPDF(authTokens.access, exportModal.filters);
      } else {
        result = await exportService.exportProcessesExcel(authTokens.access, exportModal.filters);
      }
      
      if (result.success) {
        showSuccess(result.message || `Export ${exportModal.type.toUpperCase()} réussi`);
        closeExportModal();
      } else {
        showError(result.error || `Erreur lors de l'export ${exportModal.type.toUpperCase()}`);
      }
    } catch (error) {
      showError(`Erreur lors de l'export ${exportModal.type.toUpperCase()}`);
    } finally {
      setExportModal({ ...exportModal, loading: false });
    }
  };

  const handleShowFiltersSection = () => {
    setShowFiltersSection(!showFiltersSection);
    if (showFiltersSection) {
      setShowUserDropdown(false); // Close dropdown when hiding filters
    }
  };

  const handleUserSearch = async (e) => {
    const query = e.target.value;
    handleFilterChange('assigned_user', query);
    
    if (query.length >= 2) {
      setUserSearchLoading(true);
      try {
        const result = await userService.filterUsersBySearch(query, authTokens.access);
        if (result.success) {
          setUserSearchResults(result.data);
        } else {
          setUserSearchResults([]);
        }
      } catch (error) {
        console.error('Error searching users:', error);
        setUserSearchResults([]);
      } finally {
        setUserSearchLoading(false);
      }
    } else {
      setUserSearchResults([]);
    }
  };

  const selectUser = (user) => {
    handleFilterChange('assigned_user', user.email);
    setShowUserDropdown(false);
    setUserSearchResults([]);
  };

  // Close user dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserDropdown && !event.target.closest('.relative')) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown]);

  const handleEditProcess = (process) => {
    navigate(`/dashboard/cartographies/edit/${process.id}`, { state: { process } });
  };

  const handleViewProcess = (process) => {
    navigate(`/dashboard/cartographies/${process.id}`);   
  };

  const handleViewProcessHistory = (process) => {
    navigate(`/dashboard/admin/detailedLogs/process/${process.id}`);
  };

  const handleDeleteProcess = async (processId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce processus ?')) {
      try {
        const result = await processService.deleteProcess(processId, authTokens.access);
        
        if (result.success) {
          alert('Processus supprimé avec succès!');
          loadPublishedProcesses();
        } else {
          alert(`Erreur lors de la suppression: ${result.error}`);
          console.error('Process deletion failed:', result);
        }
      } catch (error) {
        console.error('Error deleting process:', error);
        alert('Une erreur est survenue lors de la suppression du processus');
      }
    }
  };

  // // If user doesn't have permission to view all processes
  // if (!canViewAllProcesses()) {
  //   return (
  //     <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
  //       <div className="text-yellow-800">
  //         <h3 className="font-medium">Accès restreint</h3>
  //         <p className="text-sm mt-1">
  //           Vous n'avez pas les permissions nécessaires pour voir toutes les cartographies de l'organisation.
  //           Seuls les Administrateurs et les Gestionnaires de Risques peuvent accéder à cette section.
  //         </p>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <div className="space-y-6">
      {/* Header with Export Button */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h2 className="text-xl font-semibold text-gray-900">Toutes les Cartographies Publiées</h2>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleShowFiltersSection}
            className="flex items-center px-3 py-2 bg-green-500 text-white rounded-lg hover:shadow-md transition-all duration-200 text-sm"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filtres
          </button>

          {/* <button
            onClick={() => openExportModal('pdf')}
            className="flex items-center px-3 py-2 bg-red-500 text-white rounded-lg hover:shadow-md transition-all duration-200 text-sm"
          >
            <Download className="h-4 w-4 mr-2" />
            Exporter PDF
          </button>
          <button
            onClick={() => openExportModal('excel')}
            className="flex items-center px-3 py-2 bg-green-500 text-white rounded-lg hover:shadow-md transition-all duration-200 text-sm"
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Exporter Excel
          </button> */}
        </div>
      </div>

      {/* Filters Section */}
      {showFiltersSection && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className='flex items-center justify-between'>
            <div className="flex items-center mb-4">
              <Filter className="h-5 w-5 text-gray-500 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">Filtres</h3>
            </div>
            <X onClick={handleShowFiltersSection} className="h-4 w-4 mr-2 cursor-pointer" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            {/* Process Name Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom du Processus
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={filters.process_name}
                  onChange={(e) => handleFilterChange('process_name', e.target.value)}
                  placeholder="Rechercher par nom..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
              </div>
            </div>

            {/* Assigned User Filter */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Utilisateur Assigné
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={filters.assigned_user}
                  onChange={handleUserSearch}
                  onFocus={() => setShowUserDropdown(true)}
                  placeholder="Rechercher utilisateur..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8"
                />
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                
                {/* User Dropdown */}
                {showUserDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-y-auto">
                    {userSearchLoading ? (
                      <div className="px-3 py-2 text-sm text-gray-500">Recherche...</div>
                    ) : userSearchResults.length > 0 ? (
                      userSearchResults.map((user) => (
                        <div
                          key={user.id}
                          onClick={() => selectUser(user)}
                          className="px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">{user.email}</div>
                          <div className="text-xs text-gray-500">
                            {user.first_name} {user.last_name}
                          </div>
                        </div>
                      ))
                    ) : filters.assigned_user ? (
                      <div className="px-3 py-2 text-sm text-gray-500">Aucun utilisateur trouvé</div>
                    ) : (
                      <div className="px-3 py-2 text-sm text-gray-500">Tapez pour rechercher...</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Probability Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Probabilité
              </label>
              <select
                value={filters.probability}
                onChange={(e) => handleFilterChange('probability', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              >
                <option value="">Toutes les probabilités</option>
                {probabilityOptions.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            {/* Ordering Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tri
              </label>
              <select
                value={filters.ordering}
                onChange={(e) => handleFilterChange('ordering', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              >
                <option value="newest">Plus récent</option>
                <option value="oldest">Plus ancien</option>
              </select>
            </div>
          </div>

          {/* Filter Actions */}
          <div className="flex space-x-3">
            <button
              onClick={applyFilters}
              className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors duration-200 text-sm"
            >
              <Filter className="h-4 w-4 mr-2" />
              Appliquer les filtres
            </button>
            <button
              onClick={clearFilters}
              className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors duration-200 text-sm"
            >
              <X className="h-4 w-4 mr-2" />
              Effacer les filtres
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8">
          <div className="text-center">
            <div className="text-gray-500">Chargement des processus publiés...</div>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-red-700">
            <p className="font-medium">Erreur de chargement</p>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Table Section */}
      {!isLoading && !error && (
        <div className="bg-white  rounded-lg shadow-sm overflow-hidden">
            {/* {processes.map((process) => (
              <div className="overflow-x-auto border border-gray-200 mt-2">
                <ProcessListItems id={process.id} />
              </div>
            ))} */}


          <div className="overflow-x-auto border border-gray-200 mt-2">
            <table className="min-w-full bg-white text-xs border-collapse">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Nom du Processus</th>
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Date Processus</th>
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Élément à Contrôler</th>
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Point Focal</th>
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Statut</th>
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Créé par</th>
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Créé le</th>
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Mis à jour par</th>
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Mis à jour le</th>
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Nom Activité</th>
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Description Activité</th>
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Nom Risque</th>
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Description Risque</th>
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Probabilité</th>
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Consequence</th>
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Statut Risque</th>
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Action</th>
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Date Action</th>
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Assigné à</th>
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Rôle</th>
                  <th className="border border-gray-300 px-3 py-2 text-center font-semibold text-gray-700 whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {processes.map((process) => (
                  <ProcessListItems key={process.id} id={process.id} showActions={false} />
                ))}
              </tbody>
            </table>
          </div>


          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.total_pages}
            totalItems={pagination.count}
            pageSize={pagination.page_size}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            loading={isLoading}
            itemName="processus" 
          />

          {/* Empty State */}
          {processes.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium">Aucune cartographie publiée trouvée</p>
                <p className="mt-1">Il n'y a actuellement aucune cartographie publiée dans votre organisation.</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Process Details Modal (if selectedProcess) */}
      {selectedProcess && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-96 overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Détails du Processus
                </h3>
                <button 
                  onClick={() => setSelectedProcess(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nom du Processus</label>
                  <p className="text-sm text-gray-900">{selectedProcess.process_name}</p>
                </div>
                
                {selectedProcess.element_to_controle && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Élément à Contrôler</label>
                    <p className="text-sm text-gray-900">{selectedProcess.element_to_controle}</p>
                  </div>
                )}
                
                {selectedProcess.focal_point && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Point Focal</label>
                    <p className="text-sm text-gray-900">{selectedProcess.focal_point}</p>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Date du Processus</label>
                  <p className="text-sm text-gray-900">
                    {new Date(selectedProcess.process_date).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                
                {selectedProcess.associated_activities && selectedProcess.associated_activities.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Activités Associées</label>
                    <ul className="text-sm text-gray-900 list-disc list-inside">
                      {selectedProcess.associated_activities.map((activity, index) => (
                        <li key={index}>{activity.activity_name}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {exportModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Export {exportModal.type === 'pdf' ? 'PDF' : 'Excel'} - Processus
              </h3>
              <button
                onClick={closeExportModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Process Name Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom du processus
                </label>
                <input
                  type="text"
                  value={exportModal.filters.process_name}
                  onChange={(e) => handleExportFilterChange('process_name', e.target.value)}
                  placeholder="Rechercher par nom..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Statut
                </label>
                <select
                  value={exportModal.filters.status}
                  onChange={(e) => handleExportFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Tous les statuts</option>
                  <option value="draft">Brouillon</option>
                  <option value="published">Publié</option>
                  <option value="archived">Archivé</option>
                </select>
              </div>

              {/* Assigned User Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Utilisateur assigné
                </label>
                <input
                  type="text"
                  value={exportModal.filters.assigned_user}
                  onChange={(e) => handleExportFilterChange('assigned_user', e.target.value)}
                  placeholder="Email ou ID utilisateur..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Probability Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Probabilité des risques
                </label>
                <input
                  type="text"
                  value={exportModal.filters.probability}
                  onChange={(e) => handleExportFilterChange('probability', e.target.value)}
                  placeholder="Probabilité..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Creation Date Range */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Créé à partir du
                  </label>
                  <input
                    type="date"
                    value={exportModal.filters.created_date_from}
                    onChange={(e) => handleExportFilterChange('created_date_from', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Créé jusqu'au
                  </label>
                  <input
                    type="date"
                    value={exportModal.filters.created_date_to}
                    onChange={(e) => handleExportFilterChange('created_date_to', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Process Date Range */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date processus à partir du
                  </label>
                  <input
                    type="date"
                    value={exportModal.filters.process_date_from}
                    onChange={(e) => handleExportFilterChange('process_date_from', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date processus jusqu'au
                  </label>
                  <input
                    type="date"
                    value={exportModal.filters.process_date_to}
                    onChange={(e) => handleExportFilterChange('process_date_to', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Control Date Range */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date contrôle à partir du
                  </label>
                  <input
                    type="date"
                    value={exportModal.filters.control_date_from}
                    onChange={(e) => handleExportFilterChange('control_date_from', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date contrôle jusqu'au
                  </label>
                  <input
                    type="date"
                    value={exportModal.filters.control_date_to}
                    onChange={(e) => handleExportFilterChange('control_date_to', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Ordering Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tri
                </label>
                <select
                  value={exportModal.filters.ordering}
                  onChange={(e) => handleExportFilterChange('ordering', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="newest">Plus récents</option>
                  <option value="oldest">Plus anciens</option>
                </select>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={closeExportModal}
                disabled={exportModal.loading}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleExport}
                disabled={exportModal.loading}
                className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 flex items-center ${
                  exportModal.type === 'pdf' 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {exportModal.loading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                )}
                {exportModal.type === 'pdf' ? (
                  <><Download className="h-4 w-4 mr-2" />Exporter PDF</>
                ) : (
                  <><FileSpreadsheet className="h-4 w-4 mr-2" />Exporter Excel</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
